import React from "react";
import { Button } from "react-bootstrap";

// One cell in the leads right-panel 2x2 grid (Location / Buildings / Tenant /
// Landlord / Contacts). Collapsed shows an uppercase eyebrow title + a
// Change/Close control + the summary; expanded spans the full row and reveals
// its children below a dashed rule. Extracted from LeadEditPanel so other files
// (PeopleDiscoveryPanel) can render cells in the same style without a circular
// import.
const CollapsibleCell: React.FC<{
  title: React.ReactNode;
  summary: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  // Always-visible controls in the header (e.g. the owner-occupier switch).
  headerRight?: React.ReactNode;
  // Always-visible block below the picker (e.g. the landlord-research button).
  footer?: React.ReactNode;
  // Optional "view all on map" action rendered at the top of the expanded body.
  onViewAll?: () => void;
  // Full-width row span (owner-occupier merged cell).
  span?: boolean;
}> = ({
  title,
  summary,
  expanded,
  onToggle,
  children,
  headerRight,
  footer,
  onViewAll,
  span,
}) => (
  <div
    onClick={onToggle}
    style={{
      gridColumn: span || expanded ? "1 / -1" : undefined,
      border: `1px solid ${expanded ? "#9ec5fe" : "#dee2e6"}`,
      borderRadius: 8,
      padding: "10px 12px 11px",
      background: "#fff",
      cursor: "pointer",
      boxShadow: expanded
        ? "0 2px 6px rgba(13,110,253,.12), 0 6px 20px rgba(0,0,0,.06)"
        : undefined,
      minWidth: 0,
    }}
  >
    {/* Title row holds only the short eyebrow + the Change control, so the
        control never squeezes the summary content (which sits below at full
        width). */}
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 8,
        marginBottom: 5,
      }}
    >
      <div
        style={{
          minWidth: 0,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: ".04em",
          textTransform: "uppercase",
          color: "#6c757d",
        }}
      >
        {title}
      </div>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}
      >
        {headerRight}
        <Button
          size="sm"
          variant={expanded ? "outline-secondary" : "outline-primary"}
          onClick={onToggle}
          style={{
            fontSize: 12,
            fontWeight: 600,
            padding: "1px 9px",
            lineHeight: 1.4,
          }}
        >
          {expanded ? "Close" : "Change"}
        </Button>
      </div>
    </div>
    {summary}
    {expanded && (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          marginTop: 10,
          borderTop: "1px dashed #dee2e6",
          paddingTop: 10,
          cursor: "default",
        }}
      >
        {onViewAll && (
          <div style={{ textAlign: "right", marginBottom: 4 }}>
            <Button
              size="sm"
              variant="link"
              style={{ fontSize: 11, padding: 0 }}
              onClick={onViewAll}
            >
              View all on map
            </Button>
          </div>
        )}
        {children}
      </div>
    )}
    {footer && (
      <div onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
        {footer}
      </div>
    )}
  </div>
);

export default CollapsibleCell;
